/*
 * Copyright (c) 2019. ProVision Media Group Ltd. <https://provision.bg>
 * Venelin Iliev <http://veneliniliev.com>
 */

import { Injectable } from '@angular/core';
import { Observable, of } from 'rxjs';
import { HttpClient } from '@angular/common/http';
import { concatMap, map } from 'rxjs/operators';
import { environment } from '@env/environment';
import { Logger } from '@app/core/logger.service';
import { UserRole } from '@app/settings/users/roles/user-role.model';

const log = new Logger('AuthenticationService');

/**
 * Access token & Logged in user information
 */
export interface AccessToken {
  access_token: string;
  token_type: string;
  expires_in: string;
  refresh_token: string;
  profile?: UserProfile;
}

/**
 * Object Setting
 */
export interface Setting {
  name: string;
  value: any;
  collection: string;
}

export interface LoginContext {
  username: string;
  password: string;
  remember?: boolean;
}

/**
 * Profile of logged user
 */
export interface UserProfile {
  id: number;
  email: string;
  fake_first_name?: string;
  fake_last_name?: string;
  roles?: UserProfileRole[];
  settings?: Setting[];
}

/**
 * User role
 */
export interface UserProfileRole {
  id: number;
  name: string;
}

export const credentialsKey = 'credentials';

/**
 * Provides a base for authentication workflow.
 * The AccessToken interface as well as login/logout methods should be replaced with proper implementation.
 */
@Injectable()
export class AuthenticationService {
  public static permissions: Array<any> = [];

  private _credentials: AccessToken | null;
  private storage: Storage;

  /**
   *  Проверява дали юзъра има права за достъп до подадения като параметър ключ
   * @param permissionKey ключ
   */
  public static checkPermission(permissionKey: string): boolean {
    return AuthenticationService.permissions.indexOf(permissionKey) >= 0;
  }

  constructor(private http: HttpClient) {
    /*
    select current storage driver
    */
    if (sessionStorage.getItem(credentialsKey)) {
      this.storage = sessionStorage;
    } else {
      this.storage = localStorage;
    }

    const savedCredentials = this.storage.getItem(credentialsKey);
    if (savedCredentials) {
      this._credentials = JSON.parse(savedCredentials);
    }
  }

  /**
   * Authenticates the user.
   * @param context The login parameters.
   * @return The user accessToken.
   */
  login(context: LoginContext): Observable<AccessToken> {
    const postData = {
      grant_type: environment.oauth.grant_type,
      client_id: environment.oauth.client_id,
      client_secret: environment.oauth.client_secret,
      username: context.username,
      password: context.password,
    };

    return this.http.post('oauth/token', postData).pipe(
      concatMap((res: AccessToken) => {
        log.debug('set token');
        this.setAccessToken(<AccessToken>res, context.remember);
        return of(res);
      }),
      concatMap(() => {
        log.debug('load profile');
        return this.loadProfile();
      })
    );
  }

  /**
   * Logs out the user and clear accessToken.
   * @return True if the user was logged out successfully.
   */
  logout(): Observable<boolean> {
    // Customize accessToken invalidation here
    this.setAccessToken();
    return of(true);
  }

  /**
   * Checks is the user is authenticated.
   * @return True if the user is authenticated.
   */
  isAuthenticated(): boolean {
    return !!this.accessToken;
  }

  /**
   * Gets the user accessToken.
   * @return The user accessToken or null if the user is not authenticated.
   */
  get accessToken(): AccessToken | null {
    return this._credentials;
  }

  /**
   * Set user profile
   * @param profile Profile
   */
  setProfile(profile: any) {
    // this.accessToken.profile = profile;
    this._credentials.profile = profile;
    this.storage.setItem(credentialsKey, JSON.stringify(this._credentials));
  }

  /**
   * Get logged in user profile
   */
  loadProfile(): Observable<AccessToken> {
    return this.http.get('v1/profile').pipe(
      map((res: any) => {
        this._credentials.profile = <UserProfile>res.data;
        log.debug('this._credentials.profile', this._credentials.profile);
        this.storage.setItem(credentialsKey, JSON.stringify(this._credentials));

        AuthenticationService.permissions = [];
        // Пълни правата на юзъра в масива AuthenticationService.permissions
        this._credentials.profile.roles.forEach((role: UserRole) => {
          role.permissions.forEach((permission: any) => {
            if (AuthenticationService.permissions.indexOf(permission.name) < 0) {
              AuthenticationService.permissions.push(permission.name);
            }
          });
        });

        return this._credentials;
      })
    );
  }

  /**
   * Sets the user accessToken.
   * The accessToken may be persisted across sessions by setting the `remember` parameter to true.
   * Otherwise, the accessToken are only persisted for the current session.
   * @param credentials The user accessToken.
   * @param remember True to remember accessToken across sessions.
   */
  private setAccessToken(credentials?: AccessToken, remember?: boolean) {
    this._credentials = credentials || null;

    if (credentials) {
      this.storage = remember ? localStorage : sessionStorage;
      this.storage.setItem(credentialsKey, JSON.stringify(credentials));
    } else {
      sessionStorage.removeItem(credentialsKey);
      localStorage.removeItem(credentialsKey);
    }
  }
}
